import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import {
  FieldRow,
  MAX_CUSTOM_FIELDS,
  MAX_NAME_LENGTH,
  STRIPE_CHECKOUT_PREFIX
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.fieldValues`;

interface FieldValuesEditorProps {
  rows: FieldRow[];
  onChange: (rows: FieldRow[]) => void;
  disabled: boolean;
  // Names the rows for assistive tech and tests, e.g. "custom-fields".
  testId: string;
}

// Fixed Contract custom field values: a field name and the value to set.
export const FieldValuesEditor: React.FC<FieldValuesEditorProps> = ({
  rows,
  onChange,
  disabled,
  testId
}) => {
  const { t } = useTranslation();

  const updateRow = (index: number, changes: Partial<FieldRow>) =>
    onChange(
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...changes } : row
      )
    );

  return (
    <div className="flex flex-col gap-2" data-test={testId}>
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            data-test={`${testId}-name-${index}`}
            aria-label={t(`${PREFIX}.a11y.name`, { position: index + 1 })}
            placeholder={t(`${PREFIX}.name`)}
            value={row.name}
            onChange={(event) => updateRow(index, { name: event.target.value })}
            maxLength={MAX_NAME_LENGTH}
            disabled={disabled}
            containerClassName="flex-1"
          />
          <Input
            data-test={`${testId}-value-${index}`}
            aria-label={t(`${PREFIX}.a11y.value`, { position: index + 1 })}
            placeholder={t(`${PREFIX}.value`)}
            value={row.valueText}
            onChange={(event) =>
              updateRow(index, { valueText: event.target.value })
            }
            disabled={disabled}
            containerClassName="flex-1"
          />
          <IconButton
            variant="iconNoBg"
            size="lg"
            data-test={`${testId}-remove-${index}`}
            aria-label={t(`${PREFIX}.a11y.remove`, { position: index + 1 })}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            disabled={disabled}
          >
            <Trash2 />
          </IconButton>
        </div>
      ))}
      <Button
        variant="tertiary"
        className="w-fit"
        data-test={`${testId}-add`}
        onClick={() => onChange([...rows, { name: "", valueText: "" }])}
        disabled={disabled || rows.length >= MAX_CUSTOM_FIELDS}
      >
        {t(`${PREFIX}.add`)}
      </Button>
    </div>
  );
};
